import "./BarChartTable.scss";
import "../../core/table/Table.scss";
import Icon from "core/icon/Icon";
import { useState, useEffect, useRef, useMemo } from "react";
import InfosBulle from "components/infos-bulle/InfosBulle";
import PropTypes from "prop-types";
import { formatValueDate } from "utils/functions";
import {
  assembleMissionData,
  calculateEndMissionWidth,
  getLastBarIndex,
  renderAllBars,
  useMemoizedWidthByDates,
} from "utils/dashboardHelper";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

export const BarChartTable = ({
  id,
  header,
  content,
  userDate,
  firstDate,
  lastDate,
  lang,
  allWidths,
  setTableHeight,
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const datesArr = ["start_date", "end_date", "scheduled_end_date"];
  const tableRef = useRef(null);
  const TOOLTIP_SIZE = 180;
  const TOOLTIP_BOTTOM_LEFT_ADJUSTMENT = 8;
  const TOOLTIP_BOTTOM_RIGHT_ADJUSTMENT = 175;

  const handleOnMouseEnter = (index) => setHoveredRow(index);

  const handleOnMouseLeave = () => setHoveredRow(null);

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.offsetHeight);
    }
  }, [content]);

  const getWidthByDates = useMemoizedWidthByDates(
    content,
    allWidths,
    lastDate,
    userDate,
    firstDate
  );

  const getNoMissionWidth = (e) =>
    getWidthByDates({
      start: firstDate,
      end: userDate,
      additionalCheck: true,
      contentKey: "start_date",
    });

  const getOngoingMissionWidth = (e) => {
    let endBoundary = userDate;
    if (!_.isNil(e.scheduled_end_date)) {
      const scheduledEnd = new Date(e.scheduled_end_date);
      if (scheduledEnd < firstDate) {
        endBoundary = firstDate;
      } else if (scheduledEnd < userDate) {
        endBoundary = scheduledEnd;
      }
    }
    return getWidthByDates({
      start: firstDate,
      end: endBoundary,
    });
  };

  const getFuturMissionWidth = (e) =>
    getWidthByDates({
      start: userDate,
      end:
        new Date(e.scheduled_end_date) > lastDate
          ? lastDate
          : new Date(e.scheduled_end_date),
      additionalCheck: true,
      contentKey: "futur-mission",
    });

  const getOrderEndDateMissionWidth = (e) =>
    getWidthByDates({
      start: e.scheduled_end_date,
      end: !_.isNil(e.end_date) ? new Date(e.end_date) : lastDate,
      additionalCheck: true,
      contentKey: "scheduled_end_date",
    });

  const getProgressBarsWidth = (content) => {
    return content?.map((e, i) => {
      const noMission = getNoMissionWidth(e);
      const ongoingMission = getOngoingMissionWidth(e);
      const futurMission = getFuturMissionWidth(e);
      const orderEnDateMission = getOrderEndDateMissionWidth(e);
      const endMission = calculateEndMissionWidth(
        noMission[i],
        ongoingMission[i],
        futurMission[i],
        orderEnDateMission[i],
        allWidths
      );

      const widths = [
        noMission[i],
        ongoingMission[i],
        futurMission[i],
        orderEnDateMission[i],
        endMission < 10 ? 0 : endMission,
      ];

      const lastBarIndex = getLastBarIndex(widths);

      return assembleMissionData(e, widths, lastBarIndex, allWidths);
    });
  };

  const progressBars = useMemo(
    () => getProgressBarsWidth(content),
    [content, allWidths, userDate, firstDate, lastDate]
  );

  return (
    <table
      id={id}
      className="fo-table"
      ref={tableRef}
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <thead>
        <tr>
          {header.map((elm) => (
            <th
              className="bar-chart-th"
              key={elm?.id || elm.label?.value || elm.value}
              style={{ width: elm?.width }}
            >
              <div>
                {elm?.icon && <Icon color="gray-200" icon={elm?.icon} />}
                <span>{elm.label?.value}</span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {content.length > 0 ? (
          content?.map((elm, index) => (
            <tr
              key={elm?.id || index}
              className={index % 2 === 1 ? "grey-cell" : ""}
            >
              {Object.keys(elm).map((key) => {
                let hasAppliedDynamicRadiusLeft = false;
                return (
                  key !== "start_date" &&
                  key !== "end_date" && (
                    <td
                      className="bar-chart-td"
                      key={elm[key]?.id || elm?.id || `${key}-${index}`}
                      id={elm[key]?.id || elm?.id}
                      onMouseEnter={() => handleOnMouseEnter(index)}
                      onMouseLeave={handleOnMouseLeave}
                    >
                      {datesArr.includes(key) ? (
                        <div className="bar-wrapper">
                          {hoveredRow === index &&
                            progressBars?.map((e, i) => {
                              const clampTooltipTextPosition = (raw) => {
                                if (
                                  raw >
                                  allWidths.monthContainerWidth - TOOLTIP_SIZE
                                ) {
                                  return (
                                    allWidths.monthContainerWidth -
                                    TOOLTIP_BOTTOM_RIGHT_ADJUSTMENT
                                  );
                                }
                                if (raw < TOOLTIP_SIZE) {
                                  return TOOLTIP_BOTTOM_LEFT_ADJUSTMENT;
                                }
                                return raw;
                              };
                              const truePosition = clampTooltipTextPosition(
                                e.tooltipPositions.end - 10
                              );
                              const trueStartPosition = clampTooltipTextPosition(
                                e.tooltipPositions.start - 10
                              );

                              if (e.id === elm.mission_title) {
                                return [
                                  <InfosBulle
                                    key={`tooltip-start-${e.id}`}
                                    text={formatValueDate({
                                      date: e.startDate,
                                      lang,
                                    })}
                                    start={true}
                                    up={true}
                                    textPosition={trueStartPosition}
                                    position={e.tooltipPositions.start}
                                    alignLeft={
                                      !_.isNil(e.startDate) &&
                                      new Date(e.startDate) < firstDate
                                    }
                                  />,
                                  <InfosBulle
                                    key={`tooltip-end-${e.id}`}
                                    text={
                                      formatValueDate({
                                        date: e.endDate,
                                        lang,
                                      }) || "N/A"
                                    }
                                    up={false}
                                    textPosition={truePosition}
                                    position={e.tooltipPositions.end}
                                    alignRight={_.isNil(e.endDate)}
                                    alignLeft={
                                      !_.isNil(e.endDate) &&
                                      new Date(e.endDate) < firstDate
                                    }
                                  />,
                                ];
                              }
                              return [];
                            })}
                          {getProgressBarsWidth(content)?.map((e, i) => {
                            return (
                              e.id === elm.mission_title && (
                                <div key={e.id} className="bar-chart-row">
                                  {renderAllBars(
                                    e,
                                    hasAppliedDynamicRadiusLeft,
                                    allWidths
                                  )}
                                </div>
                              )
                            );
                          })}
                        </div>
                      ) : (
                        <span key={elm[key]?.id || elm?.id}>
                          {elm[key]?.display || elm[key]}
                        </span>
                      )}
                    </td>
                  )
                );
              })}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={Object.keys(header).length} className="empty-td">
              <span>
                <FormattedMessage
                  id="no_info"
                  defaultMessage="Aucune information à montrer"
                />
              </span>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

BarChartTable.propTypes = {
  id: PropTypes.string,
  header: PropTypes.array,
  content: PropTypes.array,
  userDate: PropTypes.object,
  firstDate: PropTypes.object,
  lastDate: PropTypes.object,
  lang: PropTypes.string,
  setTableHeight: PropTypes.func,
  allWidths: PropTypes.object,
};
